import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import SelectComponent from "@via/components/select/select";
import { fetchVmetaData } from "@via/pages/projects/metadata/metadata.api.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";

export const escapeRegex = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type Row = Record<string, unknown>;

const labelOf = (row: Row): string =>
  typeof row.name === "string" && row.name ? row.name : "Unnamed record";

const PAGE = 20;

interface Props {
  canvasId: string;
  collectionName: string;
  value: string | null;
  onChange: (recordId: string | null, row: Row | null) => void;
  parent?: { field: string; recordId: string } | null;
  disabled?: boolean;
  placeholder?: string;
  inputId?: string;
  /** Called when a preselected id is not readable by this viewer. */
  onUnreadable?: () => void;
}

const EventRecordPicker = ({
  canvasId,
  collectionName,
  value,
  onChange,
  parent,
  disabled,
  placeholder = "New record",
  inputId,
  onUnreadable,
}: Props) => {
  const [search, setSearch] = useState("");
  // An id the user chose from the list already reported its row; do not report it twice.
  const picked = useRef<string | null>(null);

  const searchQuery = useQuery({
    queryKey: [
      "event-picker",
      canvasId,
      collectionName,
      parent ?? null,
      search,
    ],
    queryFn: () => {
      // Plain search, never search/populated: populated rows carry a reference as the
      // parent's name, and these rows load the form, which must hold the parent's id (#1160).
      const parentField = parent ? parent.field : null;
      const filters = [
        ...(parent && parentField
          ? [{ id: parentField, value: parent.recordId }]
          : []),
        ...(search ? [{ id: "name", value: escapeRegex(search) }] : []),
      ];
      return fetchVmetaData(canvasId, collectionName, {
        filter: filters.length ? { and: filters } : undefined,
        operatorMap: parentField ? { [parentField]: "eq" } : {},
        take: PAGE,
      });
    },
    enabled: !!canvasId && !!collectionName && !disabled,
    retry: false,
  });

  // A preselected id (from a link or a save) is loaded with the viewer's own permissions.
  const selectedQuery = useQuery({
    queryKey: ["event-picker-record", canvasId, collectionName, value],
    queryFn: () =>
      fetchVmetaData(canvasId, collectionName, {
        filter: { and: [{ id: "_id", value }] },
        operatorMap: { _id: "eq" },
        take: 1,
      }),
    enabled: !!canvasId && !!collectionName && !!value,
    retry: false,
    // A refetch re-reports the row to the form; do not do that on every window focus.
    refetchOnWindowFocus: false,
  });

  const selectedRow =
    (selectedQuery.data?.data?.[0] as Row | undefined) ?? null;

  // The latest callbacks, so the effect below runs on data changes only.
  const callbacks = useRef({ onChange, onUnreadable });
  callbacks.current = { onChange, onUnreadable };

  // Only a read that succeeded and returned no row means unreadable. An error (a 5xx, a
  // dropped connection) says nothing about the record, so the id is left alone.
  useEffect(() => {
    if (!value || !selectedQuery.isSuccess || picked.current === value) return;
    if (selectedRow) callbacks.current.onChange(value, selectedRow);
    else callbacks.current.onUnreadable?.();
  }, [value, selectedQuery.isSuccess, selectedRow]);

  const options: SelectOption[] = useMemo(
    () =>
      ((searchQuery.data?.data ?? []) as Row[]).map(row => ({
        label: labelOf(row),
        value: String(row._id),
      })),
    [searchQuery.data]
  );

  const current: SelectOption | null = selectedRow
    ? { label: labelOf(selectedRow), value: String(selectedRow._id) }
    : null;

  return (
    <SelectComponent
      name={inputId ?? `${collectionName}-picker`}
      inputId={inputId}
      options={options}
      value={current}
      isSearchable
      enableClear
      readOnly={disabled}
      isLoading={searchQuery.isFetching}
      placeholder={placeholder}
      onInputChange={(text: string) => setSearch(text)}
      onChange={selected => {
        const option = selected as SelectOption | null;
        if (!option?.value) {
          picked.current = null;
          return onChange(null, null);
        }
        picked.current = String(option.value);
        const row = ((searchQuery.data?.data ?? []) as Row[]).find(
          r => String(r._id) === option.value
        );
        onChange(String(option.value), row ?? null);
      }}
    />
  );
};

export default EventRecordPicker;
